/* eslint-disable max-len */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Tooth from './Tooth'
import { IDentalData } from '@dataTypes/chartingFeature.types'
import { useAppSelector } from '@store/hooks'
import { ChartingView } from './ChartingViewToggle.component'
import ChartTooltip from './ChartTooltip'
import { getDentalDataMap } from '@utils/dental-data.utils'
import { preloadToothChartAssets } from './tooth-chart-preload.utils'
import {
  CHART_ROW_GAP,
  computeChartHeight,
  computeToothWidth,
  TOOTH_ROW_GAP,
} from './tooth-chart-layout.utils'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import { PerioSitesByTooth } from '@utils/perio-line.utils'
import ToothPerioRowConnectors, {
  PerioConnectorPart,
} from './ToothPerioRowConnectors.component'
import './ToothChart.css'

const TOOTH_TOOLTIP_ID = 'charting-tooth-tooltip'

type ToothPartOrder = Array<
  'number' | 'crownTop' | 'crownBottom' | 'base' | 'palatal' | 'lingual'
>

interface ToothChartRowGroupProps {
  toothIds: number[]
  type: 'upper' | 'lower'
  animationClass: string
  order: ToothPartOrder
  view: ChartingView
  dentalDataMap: Record<number, IDentalData[]>
  isToothSelected: (toothId: number) => boolean
  handleSelect: (toothId: number | null) => void
  treatmentPlanHighlightToothIds?: ReadonlySet<number>
  readOnly: boolean
  isPerio: boolean
  perioSitesByTooth?: PerioSitesByTooth
}

const ToothChartRowGroup = ({
  toothIds,
  type,
  animationClass,
  order,
  view,
  dentalDataMap,
  isToothSelected,
  handleSelect,
  treatmentPlanHighlightToothIds,
  readOnly,
  isPerio,
  perioSitesByTooth,
}: ToothChartRowGroupProps): React.ReactElement => {
  const rowGroupRef = useRef<HTMLDivElement | null>(null)
  const connectorParts = useMemo((): PerioConnectorPart[] => {
    if (!isPerio || !perioSitesByTooth) {
      return []
    }
    const parts: PerioConnectorPart[] = ['base']
    if (view === 'upper') {
      parts.push('palatal')
    }
    if (view === 'lower') {
      parts.push('lingual')
    }
    return parts
  }, [isPerio, perioSitesByTooth, view])

  return (
    <div
      ref={rowGroupRef}
      className={`position-relative z-1 tooth-chart-row-group ${animationClass || ''}`}
    >
      <div className="d-flex tooth-chart-row">
        {toothIds.map((toothId) => (
          <Tooth
            key={`${toothId}-${view}`}
            id={toothId}
            selected={isToothSelected(toothId)}
            onSelect={handleSelect}
            parts={order}
            dentalData={dentalDataMap[toothId] ?? []}
            view={view}
            highlightTreatmentPlan={
              treatmentPlanHighlightToothIds?.has(toothId) ?? false
            }
            readOnly={readOnly}
            perioSites={isPerio ? perioSitesByTooth?.[toothId] : undefined}
          />
        ))}
      </div>
      {perioSitesByTooth &&
        connectorParts.map((part) => (
          <ToothPerioRowConnectors
            key={`${type}-${part}-connectors`}
            containerRef={rowGroupRef}
            toothIds={toothIds}
            part={part}
            view={view}
            perioSitesByTooth={perioSitesByTooth}
          />
        ))}
    </div>
  )
}

interface ToothChartProps {
  dentalData: IDentalData[]
  selectedTooth: number | null
  setSelectedTooth: React.Dispatch<React.SetStateAction<number | null>>
  isPerio?: boolean
  /** Per-tooth PD sites when perio layer is on. */
  perioSitesByTooth?: PerioSitesByTooth
  view: ChartingView
  isAnimating?: boolean
  animationDirection?: 'up' | 'down'
  animatingRow?: 'upper' | 'lower' | null
  disableLoadingGate?: boolean
  /** When true, chart stays hidden until charting data has finished loading. */
  isDataLoading?: boolean
  /** When true, tooth SVGs were preloaded at page level (skip initial asset gate). */
  assetsPreloaded?: boolean
  /** Teeth referenced by the active treatment plan (dashed highlight when set). */
  treatmentPlanHighlightToothIds?: ReadonlySet<number>
  /** When true, teeth are not clickable (e.g. treatment plan preview chart). */
  readOnly?: boolean
  /**
   * When true, tooth width also shrinks to fit a constrained pane height (x-ray split).
   */
  allowUpscale?: boolean
  /** When true, each click toggles the tooth in selectedTeeth instead of single-select. */
  multiSelect?: boolean
  selectedTeeth?: ReadonlySet<number>
  onToggleTooth?: (toothId: number) => void
}

const ToothChart = ({
  dentalData,
  selectedTooth,
  setSelectedTooth,
  isPerio = false,
  perioSitesByTooth,
  view,
  isAnimating = false,
  animationDirection = 'up',
  animatingRow = null,
  disableLoadingGate = false,
  isDataLoading = false,
  assetsPreloaded = false,
  treatmentPlanHighlightToothIds,
  readOnly = false,
  allowUpscale = false,
  multiSelect = false,
  selectedTeeth,
  onToggleTooth,
}: ToothChartProps): React.ReactElement => {
  const { showToothNumber } = useClinicChartingPreferences()
  const upperToothOrder = useMemo((): Array<
    'number' | 'crownTop' | 'crownBottom' | 'base' | 'palatal' | 'lingual'
  > => {
    const order: Array<
      'number' | 'crownTop' | 'crownBottom' | 'base' | 'palatal' | 'lingual'
    > = ['number', 'base', 'crownTop', 'palatal']
    return showToothNumber ? order : order.filter((part) => part !== 'number')
  }, [showToothNumber])
  const lowerToothOrder = useMemo((): Array<
    'number' | 'crownTop' | 'crownBottom' | 'base' | 'palatal' | 'lingual'
  > => {
    const order: Array<
      'number' | 'crownTop' | 'crownBottom' | 'base' | 'palatal' | 'lingual'
    > = ['lingual', 'crownBottom', 'base', 'number']
    return showToothNumber ? order : order.filter((part) => part !== 'number')
  }, [showToothNumber])
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const [availableWidth, setAvailableWidth] = useState<number>(0)
  const [availableHeight, setAvailableHeight] = useState<number>(0)
  const [areAssetsReady, setAreAssetsReady] = useState<boolean>(
    disableLoadingGate || assetsPreloaded,
  )

  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const setSelectedToothRef = useRef(setSelectedTooth)
  setSelectedToothRef.current = setSelectedTooth

  useEffect(() => {
    setSelectedToothRef.current(null)
  }, [selectedPatient?.patient_id])

  useLayoutEffect(() => {
    const chartContainer = chartContainerRef.current
    if (!chartContainer) return

    const heightSlot = allowUpscale
      ? chartContainer.closest<HTMLElement>('.charting-chart-content')
      : null

    const updateAvailableSize = (): void => {
      const nextWidth = chartContainer.clientWidth
      if (nextWidth > 0) {
        setAvailableWidth(nextWidth)
      }
      if (allowUpscale) {
        const nextHeight =
          heightSlot && heightSlot.clientHeight > 0
            ? heightSlot.clientHeight
            : chartContainer.clientHeight
        setAvailableHeight(nextHeight > 0 ? nextHeight : 0)
      } else {
        setAvailableHeight(0)
      }
    }

    updateAvailableSize()

    const resizeObserver = new ResizeObserver(() => {
      updateAvailableSize()
    })
    resizeObserver.observe(chartContainer)
    if (heightSlot) {
      resizeObserver.observe(heightSlot)
    }

    const settleFrameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        updateAvailableSize()
      })
    })

    return () => {
      window.cancelAnimationFrame(settleFrameId)
      resizeObserver.disconnect()
    }
  }, [allowUpscale])

  const toothWidth = useMemo((): number => {
    return computeToothWidth({
      containerWidth: availableWidth,
      containerHeight: allowUpscale ? availableHeight : undefined,
      view,
      allowHeightFit: allowUpscale,
      showToothNumber,
    })
  }, [availableWidth, availableHeight, view, allowUpscale, showToothNumber])

  const chartHeight = useMemo((): number => {
    return computeChartHeight(toothWidth, view, showToothNumber)
  }, [toothWidth, view, showToothNumber])

  const chartLayoutStyle = useMemo((): React.CSSProperties => {
    return {
      '--tooth-width': `${toothWidth}px`,
      '--tooth-row-gap': `${TOOTH_ROW_GAP}px`,
      '--chart-row-gap': `${CHART_ROW_GAP}px`,
    } as React.CSSProperties
  }, [toothWidth])

  const dentalDataMap = useMemo((): Record<number, IDentalData[]> => {
    return getDentalDataMap(dentalData)
  }, [dentalData])

  const handleSelect = useCallback(
    (toothId: number | null): void => {
      if (toothId === null) {
        return
      }
      if (multiSelect && onToggleTooth) {
        onToggleTooth(toothId)
        return
      }
      setSelectedTooth(toothId === selectedTooth ? null : toothId)
    },
    [multiSelect, onToggleTooth, selectedTooth, setSelectedTooth],
  )
  const isToothSelected = useCallback(
    (toothId: number): boolean => {
      if (multiSelect) {
        return selectedTeeth?.has(toothId) ?? false
      }
      return selectedTooth === toothId
    },
    [multiSelect, selectedTeeth, selectedTooth],
  )

  const upperNumbers = useMemo(
    (): number[] => Array.from({ length: 16 }, (_, i) => i + 1),
    [],
  )
  const lowerNumbers = useMemo(
    (): number[] => Array.from({ length: 16 }, (_, i) => 32 - i),
    [],
  )

  // Determine which rows to render based on view
  const shouldRenderUpper = view === 'full' || view === 'upper'
  const shouldRenderLower = view === 'full' || view === 'lower'

  useEffect(() => {
    if (disableLoadingGate) {
      setAreAssetsReady(true)
      return undefined
    }

    if (assetsPreloaded) {
      return undefined
    }

    let cancelled = false
    setAreAssetsReady(false)

    void preloadToothChartAssets(view).then(() => {
      if (!cancelled) {
        setAreAssetsReady(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [disableLoadingGate, view, assetsPreloaded])

  useEffect(() => {
    if (assetsPreloaded) {
      setAreAssetsReady(true)
    }
  }, [assetsPreloaded])

  const isLayoutReady = availableWidth > 0
  const isChartHidden =
    !disableLoadingGate && (isDataLoading || !areAssetsReady || !isLayoutReady)
  // Determine animation classes for each row
  const getUpperRowAnimationClass = (): string => {
    if (!isAnimating || animatingRow !== 'upper') return ''
    return `tooth-row-animate tooth-row-${animationDirection}`
  }

  const getLowerRowAnimationClass = (): string => {
    if (!isAnimating || animatingRow !== 'lower') return ''
    return `tooth-row-animate tooth-row-${animationDirection}`
  }

  return (
    <div
      className={`my-2 box-shadow-0 border-0 bg-white tooth-chart-card${
        allowUpscale
          ? ' tooth-chart-card--xray-split'
          : ' tooth-chart-card--full-width'
      }`}
      style={chartLayoutStyle}
      ref={chartContainerRef}
    >
      <div
        className="tooth-chart-inner"
        style={{
          height: allowUpscale ? '100%' : `${chartHeight}px`,
          // Omit `visible` so a hidden ancestor (inactive charting tab) stays hidden.
          ...(isChartHidden ? { visibility: 'hidden' } : {}),
        }}
      >
        <div
          className={`d-flex flex-column align-items-center tooth-chart-stack${
            allowUpscale ? ' tooth-chart-stack--fill' : ''
          }`}
        >
          {shouldRenderUpper && (
            <ToothChartRowGroup
              toothIds={upperNumbers}
              type="upper"
              animationClass={getUpperRowAnimationClass()}
              order={upperToothOrder}
              view={view}
              dentalDataMap={dentalDataMap}
              isToothSelected={isToothSelected}
              handleSelect={handleSelect}
              treatmentPlanHighlightToothIds={treatmentPlanHighlightToothIds}
              readOnly={readOnly}
              isPerio={isPerio}
              perioSitesByTooth={perioSitesByTooth}
            />
          )}
          {shouldRenderLower && (
            <ToothChartRowGroup
              toothIds={lowerNumbers}
              type="lower"
              animationClass={getLowerRowAnimationClass()}
              order={lowerToothOrder}
              view={view}
              dentalDataMap={dentalDataMap}
              isToothSelected={isToothSelected}
              handleSelect={handleSelect}
              treatmentPlanHighlightToothIds={treatmentPlanHighlightToothIds}
              readOnly={readOnly}
              isPerio={isPerio}
              perioSitesByTooth={perioSitesByTooth}
            />
          )}
        </div>
      </div>
      <ChartTooltip id={TOOTH_TOOLTIP_ID} place="top" />
    </div>
  )
}

export default ToothChart
